/**
 * Vulnerability Query Builder - Specialized query builder for Vulnerability entities
 *
 * Builds graph queries for Vulnerability entities with special handling
 * for Risk/Vulnerability relationships and attribute-based filtering.
 *
 * @module queryBuilderCore/builders/vulnerabilityQueryBuilder
 */

import {
  DEFAULT_PAGE,
  DEFAULT_QUERY_LIMIT,
} from '@/sections/insights/queryBuilder/legacy/constants/queryDefaults';
import { transformFilterOperator } from '@/sections/insights/queryBuilder/legacy/transformers/operatorTransformer';
import { transformFilterValue } from '@/sections/insights/queryBuilder/legacy/transformers/valueTransformer';
import {
  EntityBlock,
  GraphQuery,
  QueryFilter,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/types';
import { createAttributeRelationships } from '@/sections/insights/queryBuilder/legacy/utils/attributeRelationships';
import { expandChariotGroupInSourceValues } from '@/sections/insights/queryBuilder/legacy/utils/sourceExpansion';
import { getMappedFieldName, getMappedOperator } from '@/utils/mappings.util';

/**
 * Builds a graph query specifically for Vulnerability entities
 *
 * Vulnerability queries use a Risk node as the root with INSTANCE_OF
 * relationship to Vulnerability nodes, as vulnerabilities are represented
 * as Risk instances in the graph.
 *
 * @param primaryBlock - Primary entity block (must be Vulnerability type)
 * @param assetFilterOptions - Available asset filter options
 * @param vulnFilterOptions - Available vulnerability filter options
 * @param integrationValues - List of integration source values
 * @returns Graph query structure for vulnerabilities
 */
export function buildVulnerabilityQuery(
  primaryBlock: EntityBlock,
  assetFilterOptions?: Record<string, unknown>,
  vulnFilterOptions?: Record<string, unknown>,
  integrationValues?: string[]
): GraphQuery | null {
  const allRiskFilters: QueryFilter[] = [];
  const allVulnerabilityFilters: QueryFilter[] = [];
  const allRelationships: Relationship[] = [];

  primaryBlock.filterGroups.forEach(group => {
    if (group.filters.length === 0) return;

    const groupRiskFilters: QueryFilter[] = [];
    const groupVulnerabilityFilters: QueryFilter[] = [];
    const groupRelationships: Relationship[] = [];

    group.filters.forEach(filter => {
      if (!filter.attribute) return;

      const field = getMappedFieldName(
        primaryBlock.entityType,
        filter.attribute
      );
      let operator = getMappedOperator(filter.operator || '');
      let value: string | string[] | number | number[] | boolean | boolean[] =
        Array.isArray(filter.value) ? filter.value : filter.value || '';
      let isNot = false;

      if (
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === 'object' &&
        value[0] !== null &&
        'value' in value[0]
      ) {
        value = (
          value as unknown as Array<{ value: string | number | boolean }>
        ).map(item => item.value) as string[] | number[] | boolean[];
      }

      if (operator === '!=') {
        operator = '=';
        isNot = true;
      } else if (operator === 'NOT CONTAINS') {
        operator = 'CONTAINS';
        isNot = true;
      } else if (operator === 'NOT IN') {
        operator = 'IN';
        isNot = true;
      }

      if (field === 'source' && (assetFilterOptions || vulnFilterOptions)) {
        const expandedValues = expandChariotGroupInSourceValues(
          value as string | string[],
          primaryBlock.entityType,
          assetFilterOptions,
          vulnFilterOptions,
          integrationValues
        );
        value = expandedValues;

        const sourceValues = Array.isArray(value)
          ? value.map(v => String(v))
          : [String(value)];

        groupRelationships.push({
          label: 'HAS_ATTRIBUTE',
          select_one: true,
          target: {
            labels: ['Attribute'],
            filters: [
              { field: 'name', operator: '=', value: 'source' },
              {
                field: 'value',
                operator: sourceValues.length > 1 ? 'IN' : '=',
                value: sourceValues.length > 1 ? sourceValues : sourceValues[0],
              },
            ],
          },
        });
        return;
      }

      value = transformFilterValue(field, value, primaryBlock.entityType);
      operator = transformFilterOperator(
        field,
        operator,
        primaryBlock.entityType,
        value
      );

      if (
        field === 'source' &&
        Array.isArray(value) &&
        value.length > 1 &&
        operator === '='
      ) {
        operator = 'IN';
      }

      if (['status', 'priority', 'name'].includes(field)) {
        groupRiskFilters.push({
          field: field,
          operator: operator,
          value: value,
          not: isNot,
        });
      } else if (['cve', 'cvss', 'epss', 'kev', 'exploit'].includes(field)) {
        // Map 'cve' to 'id' field on Vulnerability node
        const vulnerabilityField = field === 'cve' ? 'id' : field;
        groupVulnerabilityFilters.push({
          field: vulnerabilityField,
          operator: operator,
          value: value,
          not: isNot,
        });
      } else if (field === 'tags') {
        const tagValues = Array.isArray(value)
          ? value.map(v => String(v))
          : [String(value)];

        groupRiskFilters.push({
          field: 'tags',
          operator: 'IN',
          value: tagValues,
          not: isNot,
          reverseOperands: true,
        });
      } else {
        const attributeRelationships = createAttributeRelationships(
          field,
          operator,
          value,
          group.logicalOperator,
          isNot
        );

        attributeRelationships.forEach(rel => {
          if (rel.target?.filters) {
            rel.target.filters.forEach(f => (f.not = isNot));
          }
        });
        groupRelationships.push(...attributeRelationships);
      }
    });

    if (groupRiskFilters.length > 0) {
      if (group.logicalOperator === 'OR' && groupRiskFilters.length > 1) {
        allRiskFilters.push({
          field: '',
          operator: 'OR',
          value: groupRiskFilters,
        });
      } else {
        allRiskFilters.push(...groupRiskFilters);
      }
    }

    if (groupVulnerabilityFilters.length > 0) {
      if (
        group.logicalOperator === 'OR' &&
        groupVulnerabilityFilters.length > 1
      ) {
        allVulnerabilityFilters.push({
          field: '',
          operator: 'OR',
          value: groupVulnerabilityFilters,
        });
      } else {
        allVulnerabilityFilters.push(...groupVulnerabilityFilters);
      }
    }

    if (group.logicalOperator === 'OR' && groupRelationships.length > 1) {
      const attributeRels = groupRelationships.filter(
        r => r.label === 'HAS_ATTRIBUTE'
      );
      const otherRels = groupRelationships.filter(
        r => r.label !== 'HAS_ATTRIBUTE'
      );

      if (attributeRels.length > 1) {
        const orValue = attributeRels.map(rel => ({
          field: '',
          operator: 'AND',
          value: rel.target?.filters || [],
        }));

        const combinedAttributeRel: Relationship = {
          label: 'HAS_ATTRIBUTE',
          select_one: true,
          target: {
            labels: ['Attribute'],
            filters: [{ field: '', operator: 'OR', value: orValue }],
          },
        };
        allRelationships.push(combinedAttributeRel);
      } else if (attributeRels.length === 1) {
        allRelationships.push(attributeRels[0]);
      }

      allRelationships.push(...otherRels);
    } else {
      allRelationships.push(...groupRelationships);
    }
  });

  const rootNode = {
    labels: ['Risk'],
    filters: allRiskFilters,
    relationships: [
      ...(allVulnerabilityFilters.length > 0
        ? [
            {
              label: 'INSTANCE_OF',
              select_one: true,
              target: {
                labels: ['Vulnerability'],
                filters: allVulnerabilityFilters,
              },
            },
          ]
        : []),
      ...allRelationships,
    ],
  };

  const graphQuery: GraphQuery = {
    node: rootNode,
    limit: DEFAULT_QUERY_LIMIT,
    page: DEFAULT_PAGE,
  };

  return graphQuery;
}
